import {
  useActivateCompetition,
  useArchiveCompetition,
  useCompleteCompetition,
  useReopenAwardingCompetition,
  useRevertToActiveCompetition,
  useRevertToJudgingCompetition,
  useRevertToReviewCompetition,
  useScheduleCompetition,
  useStartAwardingCompetition,
  useStartJudgingCompetition,
  useStartReviewCompetition,
} from '@/hooks/useCompetitions';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { getApiErrorMessage } from '@/api/client';
import type { CompetitionStatus } from '@/api/types';
import { COMPETITION_LIFECYCLE_ACTIONS, type CompetitionLifecycleAction } from '@/lib/competitions';

/** One mutation per lifecycle transition, keyed like COMPETITION_LIFECYCLE_ACTIONS. */
export function useCompetitionLifecycle() {
  const mutations: Record<CompetitionLifecycleAction, ReturnType<typeof useScheduleCompetition>> = {
    schedule: useScheduleCompetition(),
    activate: useActivateCompetition(),
    startReview: useStartReviewCompetition(),
    startJudging: useStartJudgingCompetition(),
    revertToReview: useRevertToReviewCompetition(),
    startAwarding: useStartAwardingCompetition(),
    revertToJudging: useRevertToJudgingCompetition(),
    reopenAwarding: useReopenAwardingCompetition(),
    complete: useCompleteCompetition(),
    archive: useArchiveCompetition(),
    revertToActive: useRevertToActiveCompetition(),
  };
  const error = Object.values(mutations)
    .map((m) => m.error)
    .find(Boolean);
  return { mutations, error };
}

interface Props {
  competitionId: string;
  status: CompetitionStatus;
  /** The forward step gets the primary style, so the organizer sees what comes next. */
  highlightNext?: boolean;
  size?: 'sm' | 'md';
  /** Shown in the first slot of the row; the caller decides where the error goes otherwise. */
  showError?: boolean;
}

/** The buttons that move a competition along its lifecycle (Načrtuj, Aktiviraj, Začni pregled, …). */
export function CompetitionLifecycleActions({ competitionId, status, highlightNext = false, size = 'sm', showError = false }: Props) {
  const { mutations, error } = useCompetitionLifecycle();
  const actions = COMPETITION_LIFECYCLE_ACTIONS[status];
  if (actions.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {showError && error && <Alert variant="error">{getApiErrorMessage(error)}</Alert>}
      <div className="flex flex-wrap gap-2">
        {actions.map(({ action, label }, i) => {
          const m = mutations[action];
          return (
            <Button
              key={action}
              variant={highlightNext && i === 0 ? 'primary' : 'secondary'}
              size={size}
              isLoading={m.isPending && m.variables === competitionId}
              onClick={() => m.mutate(competitionId)}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
